<template>
  <PromoCard
    :offer="snippet.format(bonus.offer)"
    :title="snippet.format(bonus.title)"
    :active-button-title="buttonTitle"
    guest-button-gtm="GetBonusPromotions"
    :locked-button-title="lockedButtonTitle"
    :promocode="bonus.bonusCode"
    is-blocked-for-guests
    :ribbon-color="ribbonColor"
    :timer-end-time="bonus.finishDate || bonus.availableUntil || bonus.nextActiveDate || undefined"
    :timer-title="bonusTimerTitle"
    :ribbon-title="ribbonTitle"
    :status="bonus.status"
    :image-front="bonus.bannerCardFront"
    :status-data="statusBonusData"
    :is-active-crm-bonus="isActiveCrmBonus"
    :is-coming-soon-mode="isComingSoonMode"
    is-with-status
    @cardClick="openBonusModal"
    @buttonClick="openLink"
  />
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { PromoCard, type PromoStatusData } from '~/components/lib/promotions';
import { useSnippet } from '~/composables/useSnippet';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStorePages } from '~/stores/abstract/pages';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreCrmBonus } from '~/stores/backend/crm-bonus';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreBonusesTabs } from '~/stores/strapi/bonusesTabs';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { EBonusStatus, EBonusType } from '~/types/bonuses';
import { EModalName } from '~/types/modals';
import { SettingsId } from '~/types/settings';
import type { IBonusData } from '~/types/bonuses';
import type { TActionLinkExternal, TActionLinkInternal, TActionLinkModal } from '~/types/links';

const props = defineProps({
  bonus: {
    type: Object as PropType<IBonusData>,
    required: true,
  },
  index: {
    type: Number,
    default: 0,
  },
  isComingSoonMode: {
    type: Boolean,
    default: false,
  },
  activeTab: {
    type: String,
    default: '',
  },
});

const storeRoot = useStoreRoot();
const storeSsPlayer = useStoreSsPlayer();
const storeButtons = useStoreButtonTitles();
const storeModals = useStoreModals();
const snippet = useSnippet();
const storeComponents = useStoreComponents();
const storeBonusesTabs = useStoreBonusesTabs();
const storeCrmBonus = useStoreCrmBonus();

const buttonTitle = computed(() => props.bonus.linkAction?.title || storeButtons.getBonus);
const lockedButtonTitle = computed(() => storeButtons.moreInfo);
const ribbonColor = computed(() => {
  return storeBonusesTabs.tabs.find((el) => el.bonusType === props.bonus.bonusType)?.labelColor;
});
const ribbonTitle = computed(() => {
  return (
    props.bonus.customLabelText ||
    storeBonusesTabs.tabs.find((el) => el.bonusType === props.bonus.bonusType)?.labelTitle
  );
});

const statusBonusData = computed((): PromoStatusData | null => {
  if (props.bonus.status === EBonusStatus.locked && props.bonus.bonusType === EBonusType.Welcome) {
    return {
      statusIcon:
        props.bonus.lockedBonusCustomIcon?.url || storeComponents.bonuses.lockedBonusIcon.url,
      statusText: props.bonus.lockedBonusText || storeComponents.bonuses.bonusLockedTitle,
      status: 'inactive',
    };
  }
  return null;
});

const isActiveCrmBonus = computed(() => {
  return !!props.bonus.crmAutoCampaignId && storeCrmBonus.activatedIds.includes(props.bonus.id);
});

const bonusTimerTitle = computed(() =>
  props.bonus.finishDate || props.bonus.availableUntil
    ? storeComponents.timer.availableTitle
    : storeComponents.timer.willStartTitle
);

const openLinkModal = (action: TActionLinkModal) => {
  let timeLag = 0;

  if (action.optionalRedirect) {
    openLinkInternal({ link: action.optionalRedirect });
    // crunch because we need some time for router changes
    timeLag = 1500;
  }

  setTimeout(() => {
    const modalToOpen = action.modal as unknown as EModalName;
    const modalsWithGtm = [EModalName.signup, EModalName.deposit];
    if (action.params && action.params.length) {
      const modalParams: { [key: string]: string } = {};

      action.params.forEach((p) => {
        if (p?.param && p?.value) {
          modalParams[p.param] = p.value;
        }
      });

      storeModals.openModal(
        modalToOpen,
        modalsWithGtm.includes(modalToOpen)
          ? { ...modalParams, gtmParams: { button_place: 'bonus_promo' } }
          : modalParams
      );
    } else {
      storeModals.openModal(
        modalToOpen,
        modalsWithGtm.includes(modalToOpen) ? { gtmParams: { button_place: 'bonus_promo' } } : {}
      );
    }
  }, timeLag);
};

const openLinkInternal = ({ link }: Partial<TActionLinkInternal>) => {
  let path: string = link || '/';

  if (storeRoot.domainLang) {
    path = `/${storeRoot.domainLang}${path}`;
  }

  return navigateTo({ path });
};

const openLinkExternal = (action: TActionLinkExternal) => {
  let url: string = action.link || '/';

  if (!url.startsWith('http') && storeRoot.domainLang) {
    url = `/${storeRoot.domainLang}${url}`;
  }

  window.open(url, '_blank')?.focus();
};

const openLink = async () => {
  if (isActiveCrmBonus.value) {
    await storeCrmBonus.deActiveBonus(props.bonus);
    return;
  }
  if (props.bonus.crmAutoCampaignId) {
    storeCrmBonus.activeBonus(props.bonus);
  }

  if (props.bonus.linkAction?.length) {
    const action: TActionLinkExternal | TActionLinkInternal | TActionLinkModal =
      props.bonus.linkAction[0];

    switch (action.__component) {
      case 'link.modal': {
        openLinkModal(action);
        break;
      }
      case 'link.internal': {
        openLinkInternal(action);
        break;
      }
      case 'link.external': {
        openLinkExternal(action);
        break;
      }
      // no default
    }
  }
  // Add ondition for Estonian license only
  else if (storeRoot.isEstonianLicense && !storeSsPlayer.isDepositReady) {
    useStorePages().openAccountSettingsPage(SettingsId.Profile);
  } else {
    storeModals.openModal(EModalName.deposit, {
      bonuscode: props.bonus.bonusCode,
      bonusId: props.bonus.bonusId,
    });
  }
};

const openBonusModal = () => {
  storeModals.openModal(EModalName.bonus_promo, {
    bonusId: props.bonus.bonusId,
    activeTab: props.activeTab,
  });
};
</script>
